import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { X, ChevronRight, ChevronDown, Package, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { API } from '../config/constants';
import BottomNav from '../components/BottomNav';
import CigarImage from '../components/CigarImage';

// Helper function to display cigar name without duplicating brand
const formatCigarName = (brand, name) => {
  if (!name || name.toLowerCase() === brand?.toLowerCase()) {
    return brand || 'Unknown';
  }
  return `${brand} ${name}`;
};

const BrowseBrandsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [brands, setBrands] = useState([]);
  const [expandedBrand, setExpandedBrand] = useState(searchParams.get('brand') || null);
  const [brandCigars, setBrandCigars] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingCigars, setLoadingCigars] = useState(null);

  const fetchBrands = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cigars/brands`);
      setBrands(res.data);
    } catch (error) {
      console.error('Failed to fetch brands');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  // Load cigars for the brand from URL params on mount and scroll to it
  useEffect(() => {
    const brandFromUrl = searchParams.get('brand');
    if (brandFromUrl) {
      if (!brandCigars[brandFromUrl]) {
        fetchCigarsForBrand(brandFromUrl);
      }
      // Scroll to the brand after a short delay to allow rendering
      setTimeout(() => {
        const brandElement = document.getElementById(`brand-${brandFromUrl.replace(/\s+/g, '-')}`);
        if (brandElement) {
          brandElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [searchParams, brands]);

  const fetchCigarsForBrand = async (brand) => {
    if (brandCigars[brand]) {
      // Already loaded
      return;
    }
    
    setLoadingCigars(brand);
    try {
      const res = await axios.get(`${API}/cigars`, {
        params: { brand: brand, limit: 100 }
      });
      setBrandCigars(prev => ({
        ...prev,
        [brand]: res.data
      }));
    } catch (error) {
      console.error(`Failed to fetch cigars for ${brand}`);
    } finally {
      setLoadingCigars(null);
    }
  };

  const handleBrandClick = async (brand) => {
    if (expandedBrand === brand) {
      setExpandedBrand(null);
      // Remove brand from URL
      setSearchParams({});
    } else {
      setExpandedBrand(brand);
      // Save expanded brand to URL so back button works
      setSearchParams({ brand: brand });
      await fetchCigarsForBrand(brand);
    }
  };

  const filteredBrands = brands.filter(brand => 
    brand.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0B0B0C] flex flex-col">
      {/* Header */}
      <div className="flex items-center p-4 border-b border-white/10">
        <button onClick={() => navigate(-1)} className="text-white mr-4">
          <X size={24} />
        </button>
        <h1 className="playfair text-xl text-white flex-1 text-center pr-8">All Cigars</h1>
      </div>

      {/* Search */}
      <div className="p-4">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            type="text"
            placeholder="Search brands..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30"
          />
        </div>
      </div>

      {/* Brands List */}
      <div className="flex-1 overflow-y-auto px-4 pb-24">
        {loading ? (
          <div className="text-center text-white/50 py-8">Loading brands...</div>
        ) : filteredBrands.length === 0 ? (
          <div className="text-center text-white/50 py-8">
            <Package size={48} className="mx-auto mb-4 opacity-50" />
            <p>No brands found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredBrands.map((brand) => (
              <div 
                key={brand.name} 
                id={`brand-${brand.name.replace(/\s+/g, '-')}`}
                className="bg-white/5 rounded-xl overflow-hidden"
              >
                {/* Brand Header */}
                <button
                  onClick={() => handleBrandClick(brand.name)}
                  className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                      <span className="text-amber-500 font-bold text-lg">
                        {brand.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="text-left">
                      <h3 className="text-white font-semibold">{brand.name}</h3>
                      <p className="text-white/50 text-sm">{brand.count} cigar{brand.count !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="text-white/50">
                    {expandedBrand === brand.name ? (
                      <ChevronDown size={20} />
                    ) : (
                      <ChevronRight size={20} />
                    )}
                  </div>
                </button>

                {/* Cigars List (Expanded) */}
                {expandedBrand === brand.name && (
                  <div className="border-t border-white/10 bg-white/[0.02]">
                    {loadingCigars === brand.name ? (
                      <div className="p-4 text-center text-white/50">Loading cigars...</div>
                    ) : brandCigars[brand.name]?.length > 0 ? (
                      <div className="divide-y divide-white/5">
                        {brandCigars[brand.name].map((cigar) => (
                          <button
                            key={cigar.id}
                            onClick={() => navigate(`/cigar/${cigar.id}`)}
                            className="w-full flex items-center gap-3 p-3 pl-6 hover:bg-white/5 transition-colors"
                          >
                            <div className="w-12 h-12 bg-white/5 rounded-lg overflow-hidden flex-shrink-0">
                              <CigarImage src={cigar.image_url} alt={cigar.name} />
                            </div>
                            <div className="flex-1 text-left min-w-0">
                              <h4 className="text-white font-medium truncate">
                                {formatCigarName(cigar.brand, cigar.name)}
                              </h4>
                              <p className="text-white/50 text-sm">
                                {cigar.strength || 'Medium'} · {cigar.origin || 'Nicaragua'}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 text-amber-500">
                              <span className="font-bold">{cigar.average_rating?.toFixed(1) || '0.0'}</span>
                              <span className="text-amber-500">★</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 text-center text-white/50">No cigars found</div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      <BottomNav />
    </div>
  );
};

export default BrowseBrandsPage;
